import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useRef } from 'react';

import { V1A_NAMESPACE } from '../constants';
import { useHtmlLang } from '../hooks/use-html-lang';
import { useReducedMotion } from '../hooks/use-reduced-motion';
import { startEdge } from './edge-engine';

const FRAME_BARS = Array.from({ length: 24 }, (_value, index) => index);

const ROUTE_ROWS = [
  { code: 'SAO', ms: 3, width: '[--w:12.7%]' },
  { code: 'RIO', ms: 5, width: '[--w:17.2%]' },
  { code: 'BUE', ms: 11, width: '[--w:30.6%]' },
  { code: 'SCL', ms: 14, width: '[--w:37.3%]' },
  { code: 'MIA', ms: 19, width: '[--w:48.5%]' },
] as const;

const BLIPS = ['[--x:58%] [--y:20%] [--d:.13s]', '[--x:74%] [--y:58%] [--d:.96s]', '[--x:40%] [--y:74%] [--d:1.8s]', '[--x:22%] [--y:56%] [--d:2.29s]', '[--x:30%] [--y:30%] [--d:2.8s]'] as const;

const HEAD_CLASS = 'v1-feat__head flex items-center gap-3 *:min-w-0';
const ICON_CLASS = 'v1-feat__icon w-10 h-10 flex-none grid place-items-center relative [&_svg]:block';
const TITLE_CLASS = 'v1-feat__t m-0 text-[length:var(--fs-xl)] font-bold tracking-[-.02em] text-[#FFFFFF]';
const DESC_CLASS = 'v1-feat__d text-[length:var(--t-body)] leading-[1.55] text-[#9AA3B5] [&_strong]:text-[#FFFFFF] [&_strong]:font-semibold mt-3 mx-0 mb-0 flex-1';
const CARD_CLASS = 'v1-feat relative overflow-hidden min-w-0 pt-5 pb-[1.375rem] px-5 flex-col *:relative';

export function Edge() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const htmlLang = useHtmlLang();
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);

  // Engine strings are read through a ref so a language change does not restart the instruments.
  const stringsRef = useRef({ live: '', stable: '', withBoost: '', scanning: '', route: (code: string) => code, worse: (code: string) => code });
  stringsRef.current = {
    live: t('edge.viz.live'),
    stable: t('edge.fps.stable'),
    withBoost: t('edge.fps.with_boost'),
    scanning: t('edge.ai.scanning'),
    route: (code: string) => t('edge.routes.pill', { code }),
    worse: (code: string) => t('edge.routes.worse', { code }),
  };

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const strings = {
      get live() {
        return stringsRef.current.live;
      },
      get stable() {
        return stringsRef.current.stable;
      },
      get withBoost() {
        return stringsRef.current.withBoost;
      },
      get scanning() {
        return stringsRef.current.scanning;
      },
      route: (code: string) => stringsRef.current.route(code),
      worse: (code: string) => stringsRef.current.worse(code),
    };
    return startEdge(section, strings, htmlLang, reducedMotion);
  }, [htmlLang, reducedMotion]);

  const thousand = new Intl.NumberFormat(htmlLang).format(2000);

  return (
    <section ref={sectionRef} className='v1-edge relative isolate overflow-x-clip border-t border-[rgb(255_255_255/.08)] bg-[linear-gradient(180deg,#0B0D10,#0E1116)] py-[clamp(3rem,1.5rem_+_5vw,6rem)]' aria-labelledby='v1-edge-title'>
      <div className='v1-edge__bg' aria-hidden='true' />
      <div className='mx-auto w-full max-w-[90rem] px-[var(--gutter)]'>
        <div className='v1-head v1-head--center mx-auto max-w-[46rem] text-center [&_.v1-lead]:mx-auto'>
          <p className='v1-eyebrow v1-eyebrow--center m-0 inline-flex items-center justify-center gap-2 text-[length:var(--t-micro)] font-semibold uppercase leading-[1.3] tracking-[.16em] text-[#9AA3B5]' data-reveal>
            <span className='v1-eyebrow__dot size-1.5 flex-none rounded-full bg-[#93FF18] shadow-[0_0_10px_rgb(147_255_24/.8)]' aria-hidden='true' />
            {t('edge.eyebrow')}
          </p>
          <h2 className='v1-h2 mt-3 text-balance text-[length:var(--t-h2)] font-bold leading-[1.04] tracking-[-.032em] text-[#FFFFFF] min-[1920px]:text-[length:3.5rem]' id='v1-edge-title' data-reveal>
            <Trans t={t} i18nKey='edge.title' components={{ hl: <span className='v1-hl !text-[#93FF18] [text-shadow:0_0_22px_rgb(147_255_24/.3)] min-[360px]:whitespace-nowrap forced-colors:!text-[LinkText]' /> }} />
          </h2>
          <p className='v1-lead mt-4 max-w-[44rem] text-pretty text-[length:var(--t-lead)] leading-[1.55] text-[#CBD5E1]' data-reveal>
            {t('edge.lead')}
          </p>
        </div>

        <div className='v1-edge__grid mt-[clamp(2rem,1rem_+_3vw,3rem)] grid gap-4 *:min-w-0 md:grid-cols-2 lg:grid-cols-3 lg:gap-5' data-reveal-group>
          <article className={CARD_CLASS}>
            <div className={HEAD_CLASS}>
              <span className={ICON_CLASS} aria-hidden='true'>
                <svg viewBox='0 0 24 24' width='22' height='22' focusable='false'>
                  <path className='gi-dim' d='M3.7 17.8a9.6 9.6 0 1 1 16.6 0' strokeWidth='1.9' strokeLinecap='round' />
                  <path className='gi-dim' d='M5.9 11.6l1.9.8M12 5.1v2.1M18.1 11.6l-1.9.8' strokeWidth='1.6' strokeLinecap='round' />
                  <path className='gi-ac' d='M12 17.6 17 9.9' strokeWidth='2.4' strokeLinecap='round' />
                  <circle className='gi-acf' cx='12' cy='17.6' r='2.1' />
                </svg>
              </span>
              <h3 className={TITLE_CLASS}>FPS Boost</h3>
            </div>
            <p className={DESC_CLASS}>
              <Trans t={t} i18nKey='edge.fps.desc' components={{ strong: <strong /> }} />
            </p>
            <div className='v1-feat__viz v1-viz-fps overflow-hidden [container-type:inline-size]' aria-hidden='true' data-viz='fps' dir='ltr'>
              <p className='v1-viz__top'>
                <span className='v1-viz__label'>{t('edge.fps.label')}</span>
                <span className='v1-viz__st'>
                  <span className='v1-viz__pulse' />
                  <span data-viz-st>{t('edge.fps.stable')}</span>
                </span>
              </p>
              <div className='v1-viz-fps__row'>
                <span className='v1-viz-fps__k'>{t('edge.fps.before')}</span>
                <span className='v1-viz-fps__bar'>
                  <i className='[--w:21.6%]' />
                </span>
                <span className='v1-viz-fps__v tabular'>
                  <span data-viz-count='69'>69</span> FPS
                </span>
              </div>
              <div className='v1-viz-fps__row v1-viz-fps__row--on'>
                <span className='v1-viz-fps__k'>{t('edge.fps.with_boost')}</span>
                <span className='v1-viz-fps__bar'>
                  <i className='[--w:93.8%]' />
                </span>
                <span className='v1-viz-fps__v tabular'>
                  <span data-viz-count='300'>300</span> FPS
                </span>
              </div>
              <div className='v1-viz-fps__frames'>
                {FRAME_BARS.map((bar) => (
                  <i key={bar} />
                ))}
              </div>
              <p className='v1-viz__foot'>
                <span className='v1-viz__read'>
                  <b className='v1-viz__big tabular' data-viz-big>
                    300
                  </b>
                  <span className='v1-viz__unit'>
                    <span>fps</span>
                    <span className='v1-viz__unit-rest'>{t('edge.fps.unit_rest')}</span>
                  </span>
                </span>
                <span className='v1-viz__pill' data-viz-pill>
                  {t('edge.fps.with_boost')}
                </span>
              </p>
            </div>
          </article>

          <article className={CARD_CLASS}>
            <div className={HEAD_CLASS}>
              <span className={ICON_CLASS} aria-hidden='true'>
                <svg viewBox='0 0 24 24' width='22' height='22' focusable='false'>
                  <path className='gi-dim' d='M4.8 12C8.1 4.9 15.9 4.9 19.2 12' strokeWidth='1.7' />
                  <path className='gi-dim' d='M4.8 12c3.3 7.1 11.1 7.1 14.4 0' strokeWidth='1.7' />
                  <path className='gi-ac' d='M4.8 12h14.4' strokeWidth='2.4' strokeLinecap='round' />
                  <circle className='gi-dimf' cx='4.8' cy='12' r='2.4' />
                  <circle className='gi-acf' cx='19.2' cy='12' r='2.7' />
                </svg>
              </span>
              <h3 className={TITLE_CLASS}>{t('edge.routes.title')}</h3>
            </div>
            <p className={DESC_CLASS}>
              <Trans t={t} i18nKey='edge.routes.desc' components={{ strong: <strong /> }} />
            </p>
            <div className='v1-feat__viz v1-viz-routes overflow-hidden [container-type:inline-size]' aria-hidden='true' data-viz='routes' dir='ltr'>
              <p className='v1-viz__top'>
                <span className='v1-viz__label'>{t('edge.routes.title')}</span>
                <span className='v1-viz__st'>
                  <span className='v1-viz__pulse' />
                  <span data-viz-st>{t('edge.viz.live')}</span>
                </span>
              </p>
              {ROUTE_ROWS.map((route, index) => (
                <div key={route.code} className={index === 0 ? 'v1-viz-routes__row v1-viz-routes__row--win' : 'v1-viz-routes__row'} data-ms={route.ms}>
                  <span>{route.code}</span>
                  <span className='v1-viz-routes__bar'>
                    <i className={route.width} />
                  </span>
                  <b className='tabular'>
                    <span data-route-ms>{route.ms}</span> ms
                  </b>
                </div>
              ))}
              <p className='v1-viz__foot'>
                <span className='v1-viz__read'>
                  <b className='v1-viz__big tabular' data-viz-big>
                    3
                  </b>
                  <span className='v1-viz__unit'>
                    <span>ms</span>
                    <span className='v1-viz__unit-rest'>{t('edge.routes.unit_rest')}</span>
                  </span>
                </span>
                <span className='v1-viz__pill' data-viz-pill>
                  {t('edge.routes.pill', { code: 'SAO' })}
                </span>
              </p>
            </div>
          </article>

          <article className={`${CARD_CLASS} v1-feat--ai md:max-lg:col-span-2 md:max-lg:!grid md:max-lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] md:max-lg:grid-rows-[auto_1fr] md:max-lg:content-start md:max-lg:gap-x-[clamp(1.25rem,.5rem_+_2vw,2rem)]`}>
            <div className={`${HEAD_CLASS} md:max-lg:col-[1] md:max-lg:row-[1]`}>
              <span className={ICON_CLASS} aria-hidden='true'>
                <svg viewBox='0 0 24 24' width='22' height='22' focusable='false'>
                  <circle className='gi-dim' cx='10.8' cy='13.2' r='8.2' strokeWidth='1.7' />
                  <circle className='gi-dim gi-soft' cx='10.8' cy='13.2' r='4.2' strokeWidth='1.5' />
                  <path className='gi-ac' d='M10.8 13.2 17.9 7.6' strokeWidth='2.3' strokeLinecap='round' />
                  <circle className='gi-dimf' cx='10.8' cy='13.2' r='1.6' />
                  <circle className='gi-acf' cx='18.6' cy='7.1' r='2.7' />
                </svg>
              </span>
              <h3 className={TITLE_CLASS}>{t('edge.ai.title')}</h3>
            </div>
            <p className={`${DESC_CLASS} md:max-lg:col-[1] md:max-lg:row-[2] md:max-lg:flex-none md:max-lg:self-start`}>
              <Trans t={t} i18nKey='edge.ai.desc' components={{ strong: <strong /> }} />
            </p>
            <div className='v1-feat__viz v1-viz-ai overflow-hidden [container-type:inline-size]' aria-hidden='true' data-viz='ai' dir='ltr'>
              <p className='v1-viz__top'>
                <span className='v1-viz__label'>{t('edge.ai.label')}</span>
                <span className='v1-viz__st'>
                  <span className='v1-viz__pulse' />
                  <span data-viz-st>{t('edge.viz.live')}</span>
                </span>
              </p>
              <span className='v1-viz-ai__radar'>
                <b />
                <span className='v1-viz-ai__sweep' />
                {BLIPS.map((blip) => (
                  <span key={blip} className={`v1-viz-ai__blip ${blip}`} />
                ))}
                <em />
              </span>
              <ul className='v1-viz-ai__list'>
                <li data-viz-line>
                  <span>{t('edge.ai.servers')}</span>
                  <b className='tabular' data-viz-count='2000' data-viz-format='thousand' data-viz-suffix='+'>
                    {thousand}+
                  </b>
                </li>
                <li data-viz-line>
                  <span>{t('edge.ai.tested')}</span>
                  <b className='tabular' data-viz-count='5' data-viz-from='1' data-viz-suffix={t('edge.ai.tested_suffix')}>
                    {`5${t('edge.ai.tested_suffix')}`}
                  </b>
                </li>
                <li data-viz-line>
                  <span>{t('edge.ai.best')}</span>
                  <b className='tabular' data-viz-cycle='Miami|Santiago|Buenos Aires|Rio de Janeiro|São Paulo'>
                    São Paulo
                  </b>
                </li>
              </ul>
              <p className='v1-viz__foot' data-viz-line>
                <span className='v1-viz__read'>
                  <b className='v1-viz__big tabular' data-viz-count='3' data-viz-from='19'>
                    3
                  </b>
                  <span className='v1-viz__unit'>
                    <span>ms</span>
                    <span className='v1-viz__unit-rest'>{t('edge.ai.unit_rest')}</span>
                  </span>
                </span>
                <span className='v1-viz__pill' data-viz-pill>
                  <span className='v1-viz__pill-pre'>{t('edge.ai.pill_pre')}</span>
                  {t('edge.ai.pill')}
                </span>
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
